import events from 'events'
import { AsyncResource } from './AsyncResource'

const READY = 'ready'
const WAITING = 'waiting'
const eventNames = [READY, WAITING]

export class AsyncResourcesManager {
  static READY = READY
  static WAITING = WAITING

  constructor() {
    this.emitter = new events.EventEmitter()
    this._ready = false // for optimization purposes
    this._resources = new Map()
  }

  /**
   * @param {AsyncResource} resource
   */
  register(resource) {
    const prevState = this.isReady()

    this._resources.set(resource.id, resource)

    resource.on(AsyncResource.READY, this._onResourceReady)
    resource.on(AsyncResource.WAITING, this._onResourceWaiting)
    this._handleStateChange(prevState)
  }

  /**
   * @param {AsyncResource} resource
   */
  unregister(resourceId) {
    const prevState = this.isReady()
    const resource = this._resources.get(resourceId)
    if (!resource) {
      console.warn(`Resource: ${resourceId} not found`)
      return
    }
    this._resources.delete(resourceId)
    resource.off(AsyncResource.READY, this._onResourceReady)
    resource.off(AsyncResource.WAITING, this._onResourceWaiting)
    this._handleStateChange(prevState)
  }

  lock(id) {
    const ar = new AsyncResource(id)
    this.register(ar)

    return Promise.resolve(() => {
      ar.ready()
      this.unregister(id)
    })
  }

  isReady() {
    const resources = this._resources.values()
    return Array.from(resources).every((res) => res.isReady())
  }

  on(eventName, listener) {
    if (!eventNames.includes(eventName)) {
      throw new Error(
        `Wrong AsyncResourcesManager eventName was provided: ${eventName}`
      )
    }

    this.emitter.on(eventName, listener)

    return () => this.emitter.off(eventName, listener)
  }

  off(eventName, listener) {
    if (!listener) {
      this.emitter.removeAllListeners(eventName)
      return
    }
    this.emitter.off(eventName, listener)
  }

  destroy() {
    this.emitter.removeAllListeners()
    Array.from(this._resources.values()).forEach((resource) => {
      this.unregister(resource.id)
      resource.destroy()
    })
  }

  //
  _handleStateChange(prevState) {
    const state = this.isReady()

    if (prevState !== state) {
      this._state = state
      this.emitter.emit(state ? READY : WAITING)
    }
  }

  _onResourceReady = () => {
    this._handleStateChange(this._state)
  }

  _onResourceWaiting = () => {
    this._handleStateChange(this._state)
  }
}
